import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString()}`;
const pct = (v) => (v == null ? "—" : `${v}%`);
const fmtDt = (iso) => new Date(iso).toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });

const quarterList = () => {
  const t = new Date();
  let y = t.getFullYear();
  let q = Math.floor(t.getMonth() / 3) + 1;
  const out = [];
  for (let i = 0; i < 8; i++) {
    out.push(`${y}-Q${q}`);
    q -= 1;
    if (q === 0) { q = 4; y -= 1; }
  }
  return out;
};

export default function CommitteePack({ active }) {
  const [quarter, setQuarter] = useState(quarterList()[0]);
  const [pack, setPack] = useState(null);
  const [archives, setArchives] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadLive = useCallback(async (q) => {
    try {
      const res = await axios.get(`${API}/committee-pack?quarter=${q}`);
      setPack(res.data);
      setSnapshot(null);
    } catch {
      toast.error("Could not build the committee pack");
    }
  }, []);
  const loadArchives = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/committee-pack/archive`);
      setArchives(res.data);
    } catch { /* list stays */ }
  }, []);
  useEffect(() => {
    if (active === "pack") { loadLive(quarter); loadArchives(); }
  }, [active, quarter, loadLive, loadArchives]);

  const archiveNow = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/committee-pack/archive?quarter=${quarter}`);
      toast.success(`Pack for ${res.data.quarter} archived — dated ${fmtDt(res.data.archived_at)}`);
      loadArchives();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
    setBusy(false);
  };

  const viewArchive = async (aid) => {
    try {
      const res = await axios.get(`${API}/committee-pack/archive/${aid}`);
      setPack(res.data.pack);
      setSnapshot(res.data);
    } catch {
      toast.error("Could not open the archived pack");
    }
  };

  const k = pack?.kpi;

  return (
    <section className={`chapter utility${active === "pack" ? " active" : ""}`} id="pack">
      <div className="eyebrow">Working record · executes ECH-SOP-2.2 — the quarterly compliance committee, assembled in one click</div>
      <h1>Committee Pack</h1>
      <p className="lede">The full quarterly agenda with its evidence attached: MOCR and screening status, standing KPIs, open CAPs, the quarter's incidents, and every statutory exception. Pick the quarter, archive a dated snapshot for the record, print it, table it.</p>

      <div className="month-bar">
        <label>Quarter</label>
        <select className="inp" value={quarter} onChange={(e) => setQuarter(e.target.value)} data-testid="pack-quarter-select">
          {quarterList().map((q) => (<option key={q} value={q}>{q}</option>))}
        </select>
        <button className="btn" onClick={archiveNow} disabled={busy} data-testid="pack-archive-btn">{busy ? "Archiving…" : "Archive this pack"}</button>
        <button className="btn ghost" onClick={() => window.print()} data-testid="pack-print">Print / PDF</button>
      </div>

      {snapshot && (
        <div className="pack-snapshot-banner" data-testid="pack-snapshot-banner">
          Viewing archived snapshot of {snapshot.quarter} — frozen {fmtDt(snapshot.archived_at)}{snapshot.archived_by ? ` by ${snapshot.archived_by.name}` : ""}.{" "}
          <button className="vr-link" onClick={() => loadLive(quarter)} data-testid="pack-back-to-live">Back to live pack</button>
        </div>
      )}

      {pack && (
        <div data-testid="pack-body">
          <div className="formcard">
            <div className="formcard-title">Compliance Committee — {pack.quarter} ({pack.months[0]} → {pack.months[2]})</div>
            <p className="k-sub">Generated {fmtDt(pack.generated_at)}{pack.generated_by ? ` by ${pack.generated_by.name} (${pack.generated_by.email})` : ""}</p>
            <ol className="pack-agenda" data-testid="pack-agenda">
              {pack.agenda.map((a, i) => (<li key={i}>{a}</li>))}
            </ol>
          </div>

          <h2>Section 1 — MOCR &amp; screening status</h2>
          <table data-testid="pack-mocr-table">
            <tbody>
              <tr><th>Month</th><th>MOCR status</th><th>Exclusion screening (A-8)</th></tr>
              {pack.mocr_status.map((m, i) => {
                const s = pack.screening_status[i];
                return (
                  <tr key={m.month}>
                    <td className="q">{m.month}</td>
                    <td><span className={`vr-pill ${m.status === "CLOSED" ? "cleared" : m.status === "OPEN" ? "open" : "declined"}`}>{m.status}</span></td>
                    <td>{s.attested ? <span className="vr-pill cleared">ATTESTED — {s.screened_by}{s.matches ? ` · ${s.matches} match(es)` : ""}</span> : <span className="vr-pill declined">MISSING</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <h2>Section 2 — Standing KPIs (SOP-14.2)</h2>
          {k && (
            <>
              <div className="kpi-cards">
                <div className="kpi-card"><div className="k-label">Win rate</div><div className="k-value">{pct(k.standing.win_rate_pct)}</div><div className="k-sub">baseline 86% · {k.standing.determinations_total} determinations</div></div>
                <div className="kpi-card"><div className="k-label">Avg realization</div><div className="k-value">{pct(k.standing.avg_realization_pct)}</div><div className="k-sub">award vs billed</div></div>
                <div className="kpi-card"><div className="k-label">Awards to date</div><div className="k-value">{money(k.standing.awards_total)}</div><div className="k-sub">billed tracked {money(k.standing.billed_tracked_total)}</div></div>
                <div className="kpi-card"><div className="k-label">Overdue windows</div><div className="k-value">{k.standing.overdue_statutory_windows}</div><div className="k-sub">standard: zero</div></div>
              </div>
              <table data-testid="pack-kpi-table">
                <tbody>
                  <tr><th>Month</th><th>Remittances</th><th>Determinations</th><th>Win rate</th><th>Realization</th><th>Awards</th><th>Staffed</th><th>Cash</th></tr>
                  {k.months.map((m) => (
                    <tr key={m.month}>
                      <td className="q">{m.month}{m.adjusted ? " *" : ""}</td>
                      <td>{m.new_remittances}</td>
                      <td>{m.determinations}</td>
                      <td>{pct(m.win_rate_pct)}</td>
                      <td>{pct(m.avg_realization_pct)}</td>
                      <td>{money(m.awards_sum)}</td>
                      <td>{m.cases_staffed}</td>
                      <td>{m.cash_collected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="k-sub">* month carries manual adjustments (see KPI Dashboard for the computed values).</p>
            </>
          )}

          <h2>Section 3 — Open CAPs ({pack.open_caps.length}){pack.caps_closed_in_quarter.length ? ` · ${pack.caps_closed_in_quarter.length} closed this quarter` : ""}</h2>
          <table data-testid="pack-caps-table">
            <tbody>
              <tr><th>CAP #</th><th>Finding</th><th>Owner</th><th>Deadline</th><th>Status</th></tr>
              {pack.open_caps.length === 0 ? (
                <tr><td colSpan={5}>No open CAPs.</td></tr>
              ) : pack.open_caps.map((c) => (
                <tr key={c.id}>
                  <td className="q">{c.cap_number}</td>
                  <td>{c.finding}</td>
                  <td>{c.owner}</td>
                  <td>{c.deadline}</td>
                  <td><span className={`vr-pill ${c.overdue ? "declined" : "open"}`}>{c.overdue ? "OVERDUE" : "OPEN"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Section 4 — Incidents this quarter ({pack.incidents_in_quarter.length}){pack.open_incidents.length ? ` · ${pack.open_incidents.length} still open overall` : ""}</h2>
          <table data-testid="pack-incidents-table">
            <tbody>
              <tr><th>INC #</th><th>Date</th><th>Type</th><th>Sev.</th><th>Description</th><th>Status</th></tr>
              {pack.incidents_in_quarter.length === 0 ? (
                <tr><td colSpan={6}>No incidents logged in {pack.quarter}.</td></tr>
              ) : pack.incidents_in_quarter.map((i) => (
                <tr key={i.id}>
                  <td className="q">{i.incident_number}</td>
                  <td>{i.date}</td>
                  <td>{i.incident_type}</td>
                  <td>{i.severity}</td>
                  <td>{i.description}</td>
                  <td><span className={`vr-pill ${i.status === "CLOSED" ? "cleared" : "open"}`}>{i.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Section 5 — Statutory exceptions</h2>
          <table data-testid="pack-exceptions-table">
            <tbody>
              <tr><th>Kind</th><th>Item</th><th>Detail</th></tr>
              {pack.exceptions.overdue_statutory_windows.map((c) => (
                <tr key={`w-${c.claim_id}`}><td><span className="vr-pill declined">IDR WINDOW</span></td><td className="q">{c.claim_id}</td><td>{c.stage} — deadline {c.next_deadline} passed ({Math.abs(c.days_left)} bd ago)</td></tr>
              ))}
              {pack.exceptions.overpayments_open.map((o) => (
                <tr key={`o-${o.id}`}><td><span className={`vr-pill ${o.status === "OVERDUE" ? "declined" : "open"}`}>OVERPAYMENT</span></td><td className="q">{o.claim_id}</td><td>{money(o.amount)} — 60-day deadline {o.deadline} ({o.status})</td></tr>
              ))}
              {pack.exceptions.baas_flagged.map((b) => (
                <tr key={`b-${b.id}`}><td><span className={`vr-pill ${b.status === "LAPSED" ? "declined" : "open"}`}>BAA</span></td><td>{b.vendor}</td><td>{b.status} — renewal {b.renewal_date}</td></tr>
              ))}
              {pack.exceptions.overdue_statutory_windows.length + pack.exceptions.overpayments_open.length + pack.exceptions.baas_flagged.length === 0 && (
                <tr><td colSpan={3}>No statutory exceptions — clean quarter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <h2>Pack archive {archives.length ? `— ${archives.length} dated snapshot${archives.length === 1 ? "" : "s"}` : ""}</h2>
      <table data-testid="pack-archive-table">
        <tbody>
          <tr><th>Quarter</th><th>Archived</th><th>By</th><th>At archive time</th><th></th></tr>
          {archives.length === 0 ? (
            <tr><td colSpan={5}>No archived packs yet — archive one above so the quarter stays reviewable as-tabled.</td></tr>
          ) : archives.map((a) => (
            <tr key={a.id}>
              <td className="q">{a.quarter}</td>
              <td>{fmtDt(a.archived_at)}</td>
              <td>{a.archived_by ? a.archived_by.name : "—"}</td>
              <td className="k-sub">{a.summary.open_caps} open CAPs · {a.summary.incidents_in_quarter} incidents · {a.summary.exceptions} exceptions · win rate {pct(a.summary.win_rate_pct)}</td>
              <td><button className="vr-link" onClick={() => viewArchive(a.id)} data-testid={`pack-archive-view-${a.id}`}>View snapshot</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Archived packs are frozen as-generated — later register changes never rewrite a tabled pack. Minutes and CAP assignments go back into the CAP and Verify Registers. Retention: 10 years (SOP 2.2 / A-7).</p>
    </section>
  );
}
